import type { DateOnly } from '@/domain/dates'

/**
 * Domain model shared by the UI, the mock API and (later) the Hono worker.
 * Names mirror the planned Postgres schema (organizations, memberships, employees…).
 */

export type Role = 'admin' | 'hr' | 'manager' | 'employee'
export type LeaveType = 'vacation' | 'birthday'
export type RequestStatus = 'pending' | 'approved' | 'rejected' | 'cancelled'
export type ChangeKind = 'modify' | 'cancel'
export type CustomDayKind = 'holiday' | 'bridge'

export interface OrgSettings {
  /** Annual vacation days by contract (Código do Trabalho art. 238.º: 22 working days). */
  annualDays: number
  /** Minimum consecutive working days in one period per year (legal rule, default 10). */
  minConsecutiveDays: number
  carryOverEnabled: boolean
  /** "MM-DD": last day carried-over days can be used (inclusive). */
  carryOverDeadline: string
  /** Days before the deadline when reminders start. */
  carryOverReminderDays: number
  /** Birthday day off enabled. */
  birthdayDayOff: boolean
  timezone: string
}

export const DEFAULT_SETTINGS: OrgSettings = {
  annualDays: 22,
  minConsecutiveDays: 10,
  carryOverEnabled: true,
  carryOverDeadline: '04-30',
  carryOverReminderDays: 14,
  birthdayDayOff: true,
  timezone: 'Europe/Lisbon',
}

export interface Organization {
  id: string
  name: string
  settings: OrgSettings
  createdAt: string
}

export interface Membership {
  organization: Organization
  role: Role
  /** The employee record of this user in the organization (null for pure admins). */
  employeeId: string | null
}

export interface Department {
  id: string
  name: string
  managerId: string | null
  /** Max people away at the same time (null = no limit). */
  maxConcurrent: number | null
}

export interface Employee {
  id: string
  name: string
  email: string
  role: Role
  departmentId: string | null
  managerId: string | null
  /** Overrides the organization's annualDays for this person. */
  annualDays: number | null
  birthDate: DateOnly | null
  hireDate: DateOnly | null
  active: boolean
}

export interface LeaveRequest {
  id: string
  employeeId: string
  type: LeaveType
  start: DateOnly
  end: DateOnly
  workingDays: number
  status: RequestStatus
  comment: string | null
  createdAt: string
  createdBy: string
  decidedBy: string | null
  decidedAt: string | null
  decisionComment: string | null
}

export interface ChangeRequest {
  id: string
  requestId: string
  employeeId: string
  kind: ChangeKind
  newStart: DateOnly | null
  newEnd: DateOnly | null
  newWorkingDays: number | null
  reason: string
  status: RequestStatus
  createdAt: string
  decidedBy: string | null
  decidedAt: string | null
  decisionComment: string | null
}

export interface BalanceRecord {
  employeeId: string
  year: number
  contractDays: number | null
  carriedOver: number
  notes: string | null
}

export interface CustomDay {
  id: string
  date: DateOnly
  name: string
  kind: CustomDayKind
}

export interface Balance {
  year: number
  contractDays: number
  /** Carried-over days still valid today. */
  carryOver: number
  carryOverOriginal: number
  carryOverUsed: number
  /** Carried-over days still available after approved requests. */
  carryOverLeft: number
  carryOverDeadline: DateOnly
  carryOverExpired: boolean
  /** Contract days used (approved). */
  used: number
  /** Contract days in pending requests. */
  pending: number
  /** Contract days still free to plan. */
  remaining: number
  /** remaining + carryOverLeft (what a new request can use). */
  available: number
}

export interface ActivityItem {
  id: string
  at: string
  actorName: string
  text: string
}
